import { io } from 'socket.io-client';
import { WEBSOCKET_URL, WS_EVENTS, CHAT_CONFIG } from './chatConfig';

/**
 * WebSocket сервис для работы с чатом в реальном времени
 */

class ChatWebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.orderId = null;
    this.email = null;
  }

  /**
   * Подключение к WebSocket серверу
   */
  connect() {
    if (this.socket && this.isConnected) {
      console.log('Already connected to WebSocket');
      return this.socket;
    }

    this.socket = io(WEBSOCKET_URL, {
      reconnectionAttempts: CHAT_CONFIG.reconnectionAttempts,
      reconnectionDelay: CHAT_CONFIG.reconnectionDelay,
      autoConnect: CHAT_CONFIG.autoConnect,
    });

    this.setupEventListeners();
    
    return this.socket;
  }

  /**
   * Настройка базовых обработчиков событий
   */
  setupEventListeners() {
    this.socket.on('connect', () => {
      console.log('Connected to WebSocket server');
      this.isConnected = true;
    });

    this.socket.on('disconnect', () => {
      console.log('Disconnected from WebSocket server');
      this.isConnected = false;
    });

    this.socket.on(WS_EVENTS.CONNECT_ERROR, (error) => {
      console.error('WebSocket connection error:', error);
    });

    this.socket.on(WS_EVENTS.ERROR, (data) => {
      console.error('WebSocket error:', data.message);
    });
  }

  /**
   * Подключение пользователя к чату заказа
   * @param {string} orderId - ID заказа
   * @param {string} email - Email пользователя
   * @param {Function} onJoined - Callback при успешном подключении
   */
  joinOrderChat(orderId, email, onJoined) {
    if (!this.socket) {
      this.connect();
    }

    this.orderId = orderId;
    this.email = email;

    this.socket.emit(WS_EVENTS.USER_JOIN, {
      orderId,
      email,
    });

    // Обработчик успешного подключения
    this.socket.once(WS_EVENTS.CHAT_JOINED, (data) => {
      console.log('Joined chat:', data.orderId);
      if (onJoined) {
        onJoined(data);
      }
    });
  }

  /**
   * Отправка сообщения
   * @param {string} message - Текст сообщения
   * @param {Function} onSent - Callback при успешной отправке
   */
  sendMessage(message, onSent) {
    if (!this.socket || !this.isConnected) {
      console.error('Not connected to WebSocket');
      return;
    }

    this.socket.emit(WS_EVENTS.USER_MESSAGE, {
      message,
    });

    // Обработчик подтверждения отправки
    if (onSent) {
      this.socket.once(WS_EVENTS.MESSAGE_SENT, (data) => {
        onSent(data);
      });
    }
  }

  /**
   * Подписка на сообщения от агента
   * @param {Function} callback - Callback для обработки сообщений
   */
  onAgentMessage(callback) {
    if (!this.socket) {
      this.connect();
    }

    this.socket.on(WS_EVENTS.AGENT_MESSAGE, (data) => {
      console.log('Received agent message:', data.message);
      if (callback) {
        callback(data);
      }
    });
  }

  /**
   * Отписка от сообщений агента
   */
  offAgentMessage() {
    if (this.socket) {
      this.socket.off(WS_EVENTS.AGENT_MESSAGE);
    }
  }

  /**
   * Отключение от WebSocket
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.orderId = null;
      this.email = null;
    }
  }

  /**
   * Получение статуса подключения
   * @returns {boolean}
   */
  getConnectionStatus() {
    return this.isConnected;
  }

  /**
   * Получение текущего socket
   * @returns {Socket|null}
   */
  getSocket() {
    return this.socket;
  }
}

// Экспортируем singleton instance
const chatWebSocketService = new ChatWebSocketService();
export default chatWebSocketService;
